<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { IWarehouse } from '@nova/core';

import CheckinStepFooter from '@/components/CheckinStepFooter.vue';
import CheckinDetails from '@/components/CheckinDetails.vue';
import { CheckinFlowStep, useCheckinFlow } from '@/composables/useCheckinFlow';
import { resolveIdVerificationSettings } from '@/composables/useIdVerificationSettings';
import { useCheckinSubmission } from '@/composables/useCheckinSubmission';

defineOptions({ name: 'CheckinDetailsPage' });

const props = defineProps<{
  warehouse: Partial<IWarehouse>;
}>();

const router = useRouter();
const { t } = useI18n();
const checkinFlow = useCheckinFlow();
const warehouseRef = toRef(props, 'warehouse');
const { isSubmitting, inlineError, submit } = useCheckinSubmission(warehouseRef);

const detailsRef = ref<InstanceType<typeof CheckinDetails> | null>(null);

const goesToIdVerification = computed(
  () => resolveIdVerificationSettings(props.warehouse, checkinFlow.loadTypeId.value).isEnabled
);

const nextLabel = computed(() =>
  goesToIdVerification.value ? t('common.verifyAndCheckIn') : t('common.complete')
);

const nextIcon = computed(() => (goesToIdVerification.value ? 'mdi-arrow-right' : 'mdi-check'));

watch(
  () => detailsRef.value?.isFormValid,
  valid => checkinFlow.setStepValid(CheckinFlowStep.CheckinDetailsPage, Boolean(valid)),
  { immediate: true }
);

function handlePrevious() {
  checkinFlow.setPreviousStep(checkinFlow.previousStep.value ?? CheckinFlowStep.Welcome);
}

function handleNext() {
  if (goesToIdVerification.value) {
    checkinFlow.setNextStep(CheckinFlowStep.IdVerification);
    return;
  }

  submit(router);
}
</script>

<template>
  <div class="comet-i18n-exclude flex-grow-1 d-flex flex-column justify-space-between gap-6">
    <div v-if="inlineError" class="id-verification__error">
      <v-alert type="warning" icon="mdi-alert-circle-outline" prominent>
        {{ inlineError.title }}
      </v-alert>
    </div>

    <checkin-details ref="detailsRef" :warehouse="warehouse" />

    <checkin-step-footer
      :next-disabled="!checkinFlow.isStepValid(CheckinFlowStep.CheckinDetailsPage)"
      :next-loading="isSubmitting"
      :next-label="nextLabel"
      :next-icon="nextIcon"
      @previous="handlePrevious"
      @next="handleNext" />
  </div>
</template>
